import React from 'react';
import { useIntl } from 'react-intl';
import { components, MenuProps, OptionProps, ValueContainerProps } from 'react-select';
import { ReactComponent as IconCross } from '@whitelabel/media/icons/small/cross.svg';
import { ReactComponent as IconSearch } from '@whitelabel/media/icons/small/search.svg';
import { ISelectOption, ISelectInput } from '@whitelabel/helpers/types';
import { StyledSelect } from '../../styled/StyledSelect';
import { StyledSelectLoadingIcon } from './styled';

// onChange from Redux Form Field has to be called explicity.
const singleChangeHandler = (func: any) => (value: any) => func(value ? value.value : '');

// onBlur from Redux Form Field has to be called explicity.
const multiChangeHandler = (func: any) => (values: any) => func(values.map((value: any) => value.value));

/**
 * For single select, Redux Form keeps the value as a string, while React Select
 * wants the value in the form { value: "AU", label: "Australia" }
 *
 * For multi select, Redux Form keeps the value as array of strings, while React Select
 * wants the array of values in the form [{ value: "AU", label: "Australia" }]
 */
const transformValue = (value: any, options: any, isMulti: any) => {
  if (isMulti && typeof value === 'string') {
    return [];
  }

  const filteredOptions = options.filter((option: any) =>
    isMulti ? value.indexOf(option.value) !== -1 : option.value === value,
  );
  return isMulti ? filteredOptions : filteredOptions[0];
};

// TODO: Update ISelectInputProps
export interface ISelectInputProps {
  input: ISelectInput;
  id: string;
  options?: ISelectOption[];
  isMulti?: boolean;
  invalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
  reverseSelectionArrow?: boolean;
  isSearchable?: boolean;
  customComponents?: {
    Menu?: (props: MenuProps<Record<string, unknown>, false>) => JSX.Element;
    Option?: (props: OptionProps<Record<string, unknown>, false, any>) => JSX.Element;
    ValueContainer?: ({ children }: ValueContainerProps<ISelectOption, false>) => JSX.Element;
  };
  customSelectProps?: {
    [key: string]: unknown;
  };
  customOptionLabel?: (options: { value: string; label: string }) => string;
  useSingleValue?: boolean;
  instanceId?: string; // required by Nextjs
}

const SelectInput = ({
  input,
  id,
  options,
  isMulti = false,
  disabled = false,
  reverseSelectionArrow = false,
  isSearchable = true,
  customComponents,
  customSelectProps,
  customOptionLabel,
  useSingleValue = true,
  instanceId,
  ...props
}: ISelectInputProps): JSX.Element => {
  const intl = useIntl();
  const handleChange = isMulti ? multiChangeHandler(input.onChange) : singleChangeHandler(input.onChange);
  const MultiValueRemove = (parentProps: any) => (
    <components.MultiValueRemove {...parentProps}>
      <IconCross />
    </components.MultiValueRemove>
  );
  const DropdownIndicator = () => <IconSearch />;

  const LoadingIndicator = () => <StyledSelectLoadingIcon />;

  const SingleValue = (selectProps: any) => {
    const {
      data: { selectLabel, label },
    } = selectProps;
    return <components.SingleValue {...selectProps}>{selectLabel || label}</components.SingleValue>;
  };
  const handleBlur = () => input.onBlur && input.onBlur(input.value);

  const getComponentsProp = () =>
    isMulti
      ? {
          ...customComponents,
          DropdownIndicator,
          MultiValueRemove,
        }
      : {
          ...customComponents,
          LoadingIndicator,
          ...(useSingleValue && { SingleValue }),
        };

  return (
    <StyledSelect
      reverseSelectionArrow={reverseSelectionArrow}
      {...input}
      $rtl={intl.bidi}
      isSearchable={isSearchable}
      id={id}
      valueKey="value"
      value={transformValue(input.value, options, isMulti)}
      isMulti={isMulti}
      options={options}
      onChange={handleChange}
      onBlur={handleBlur}
      classNamePrefix="react-select"
      components={getComponentsProp()}
      getOptionLabel={customOptionLabel}
      isDisabled={disabled}
      instanceId={instanceId}
      {...customSelectProps}
      {...props}
    />
  );
};

export default SelectInput;
